import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import type { SelectOption } from 'naive-ui';
import {
  createContractType as createContractTypeApi,
  deleteContractType as deleteContractTypeApi,
  listContractTypes,
  updateContractType as updateContractTypeApi,
  type ContractTypeCreate,
  type ContractTypeInDb,
  type ContractTypeTemplateKey,
  type ContractTypeUpdate,
} from '@/api';
import { logger } from '@/utils/logger';

/**
 * The organisation's contract types — the one list every type picker and
 * filter reads from.
 *
 * Contracts, playbooks and clauses are *written* by uid, but saved-view and
 * calendar filters match on the type's *name* (the backend keeps them that way
 * so a filter survives a type being recreated), hence the two option sets.
 *
 * Writes throw so the calling form can show the backend's reason inline
 * (e.g. a duplicate name); only the list fetch swallows and logs.
 */
export const useContractTypeStore = defineStore('contractType', () => {
  const contractTypes = ref<ContractTypeInDb[]>([]);
  const loading = ref<boolean>(false);
  /** Distinguishes "the org has no types" from "not fetched yet". */
  const loaded = ref<boolean>(false);
  let inFlight: Promise<void> | null = null;

  const sortedContractTypes = computed<ContractTypeInDb[]>(() =>
    [...contractTypes.value].sort((a, b) => a.name.localeCompare(b.name))
  );

  /** For pickers that write a contract type: the value is the uid. */
  const uidOptions = computed<SelectOption[]>(() =>
    sortedContractTypes.value.map(type => ({ label: type.name, value: type.uid }))
  );

  /** For filters, which match on the name. */
  const nameOptions = computed<SelectOption[]>(() =>
    sortedContractTypes.value.map(type => ({ label: type.name, value: type.name }))
  );

  /** The org's copy of a code template, if it still has one. */
  function byKey(key: ContractTypeTemplateKey): ContractTypeInDb | undefined {
    return contractTypes.value.find(type => type.key === key);
  }

  async function fetchContractTypes(): Promise<void> {
    loading.value = true;
    try {
      const response = await listContractTypes();
      contractTypes.value = response.data ?? [];
      loaded.value = true;
    } catch (error_) {
      logger.error('Error fetching contract types:', error_);
    } finally {
      loading.value = false;
    }
  }

  /** Fetches once; concurrent callers share the same request. */
  function ensureLoaded(): Promise<void> {
    if (loaded.value) return Promise.resolve();
    inFlight ??= fetchContractTypes().finally(() => {
      inFlight = null;
    });
    return inFlight;
  }

  async function createContractType(body: ContractTypeCreate): Promise<ContractTypeInDb> {
    const response = await createContractTypeApi({ body });
    if (!response.data) throw new Error('Failed to create contract type');
    contractTypes.value.push(response.data);
    return response.data;
  }

  async function updateContractType(
    uid: string,
    body: ContractTypeUpdate
  ): Promise<ContractTypeInDb> {
    const response = await updateContractTypeApi({ path: { uid }, body });
    if (!response.data) throw new Error('Failed to update contract type');
    const index = contractTypes.value.findIndex(type => type.uid === uid);
    if (index !== -1) contractTypes.value[index] = response.data;
    return response.data;
  }

  /** `replacementUid` moves the type's contracts, playbooks and clauses onto that type. */
  async function deleteContractType(uid: string, replacementUid?: string | null): Promise<void> {
    await deleteContractTypeApi({
      path: { uid },
      query: replacementUid ? { replacement_uid: replacementUid } : undefined,
    });
    contractTypes.value = contractTypes.value.filter(type => type.uid !== uid);
  }

  function clearAll(): void {
    contractTypes.value = [];
    loaded.value = false;
  }

  return {
    contractTypes,
    loading,
    loaded,
    sortedContractTypes,
    uidOptions,
    nameOptions,
    byKey,
    fetchContractTypes,
    ensureLoaded,
    createContractType,
    updateContractType,
    deleteContractType,
    clearAll,
  };
});
